
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "@/components/ui/use-toast";
import Navbar from "@/components/Navbar";

const formSchema = z.object({
  companyName: z.string().min(2, {
    message: "Company/Organization name must be at least 2 characters."
  }),
  individualName: z.string().min(2, {
    message: "Individual name must be at least 2 characters."
  }),
  role: z.string().min(2, {
    message: "Role must be at least 2 characters."
  }),
  industry: z.string().min(2, {
    message: "Industry must be at least 2 characters."
  }),
  email: z.string().email({
    message: "Please enter a valid email address."
  }),
  scheduleBriefing: z.boolean().default(false),
  authorizeCompanyName: z.boolean().default(false),
  includeMyName: z.boolean().default(false),
  inviteFriend: z.string().optional(),
  comments: z.string().optional(),
});

const IspaiWaitlist = () => {
  const navigate = useNavigate();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: "",
      individualName: "",
      role: "",
      industry: "",
      email: "",
      scheduleBriefing: false,
      authorizeCompanyName: false,
      includeMyName: false,
      inviteFriend: "",
      comments: ""
    }
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    console.log("Waitlist form submitted:", values);
    
    toast({
      title: "Welcome to the iSPAI Waitlist!",
      description: "Thank you for joining. We'll be in touch soon with updates."
    });
    
    // Navigate back to intro or a confirmation page
    navigate("/intro");
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <Card className="border-2 border-black">
            <CardHeader className="bg-slate-100 border-b border-black">
              <CardTitle className="font-serif text-2xl text-center">
                Join iSPAI Waitlist - Consumer
              </CardTitle>
              <p className="text-center text-gray-600 mt-2">
                Register your organization to be among the first to access iSPAI's knowledge solutions.
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="companyName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-serif text-lg">Company or Organization Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter company/organization name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="individualName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-serif text-lg">Individual Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter your full name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-serif text-lg">Role *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter your role/title" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="industry"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-serif text-lg">Industry *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter your industry" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-serif text-lg">Email *</FormLabel>
                        <FormControl>
                          <Input placeholder="you@company.com" type="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="scheduleBriefing"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="font-serif">
                              Schedule a Briefing with iSPAI
                            </FormLabel>
                          </div>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="authorizeCompanyName"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="font-serif">
                              Authorize including company name on iSPAI waitlist
                            </FormLabel>
                          </div>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="includeMyName"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="font-serif">
                              Include my name on the waitlist
                            </FormLabel>
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="inviteFriend"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-serif text-lg">Invite a friend or colleague to this introduction</FormLabel>
                        <FormControl>
                          <Input placeholder="friend@company.com" type="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="comments"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-serif text-lg">Comments</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Any additional information or questions..." 
                            className="min-h-24" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="flex justify-between pt-4">
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => navigate("/intro")}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="px-8">
                      Join Waitlist
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default IspaiWaitlist;
